// Google Tag Manager — chargement + push d'événements e-commerce (GA4).
//
// GTM n'est chargé QUE si `VITE_GTM_ID` est défini (prod uniquement). En dev et
// staging la variable est vide : `initGtm()` et `pushPurchase()` sont des no-ops,
// donc aucun tracking ne part de nos tests locaux ni de la préprod.
//
// L'app étant une SPA, il n'y a pas de rechargement de page : le snippet GTM est
// injecté une fois ici, et l'événement `purchase` est poussé manuellement quand
// la page de confirmation s'affiche (voir use-track-purchase-once.ts).
//
// TODO(consent): brancher un Consent Mode / une bannière cookies (RGPD/CNIL)
// avant de charger GTM en production.

const GTM_ID = import.meta.env.VITE_GTM_ID as string | undefined

type DataLayerEntry = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer?: DataLayerEntry[]
  }
}

export interface LeadPayload {
  /** Référence du dossier — sert aussi de clé de déduplication. */
  transactionId: string
  currency?: string
}

export interface PurchaseItem {
  item_name: string
  price: number
  quantity: number
}

export interface PurchasePayload {
  transactionId: string
  currency: string
  items: PurchaseItem[]
  value: number
}

/**
 * Injecte le snippet GTM (équivalent du snippet `<head>` officiel) le plus tôt
 * possible. À appeler une seule fois au bootstrap de l'app.
 */
export function initGtm(): void {
  if (!GTM_ID || typeof window === 'undefined' || typeof document === 'undefined') {
    return
  }

  const dataLayer = getDataLayer()
  dataLayer.push({ event: 'gtm.js', 'gtm.start': new Date().getTime() })

  const firstScript = document.getElementsByTagName('script')[0]
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`
  firstScript?.parentNode?.insertBefore(script, firstScript)
}

/**
 * Pousse un événement nommé pour une étape du tunnel de réservation, afin que
 * GTM/GA4 distingue chaque étape (au lieu d'un `page_view` générique unique).
 */
export function pushFunnelStep(event: string, params?: DataLayerEntry): void {
  if (!GTM_ID || typeof window === 'undefined') {
    return
  }

  const dataLayer = getDataLayer()
  dataLayer.push({ event, ...params })
}

/**
 * Pousse un événement `generate_lead` sur la page de confirmation d'un dossier
 * en paiement différé (virement, chèques-vacances, espèces…). Contrairement à
 * `purchase`, aucun montant n'est envoyé : aucune transaction n'est encaissée à
 * ce stade, donc le CA (somme des `purchase`) reste juste. L'event sert
 * uniquement à ne pas perdre la conversion Google Ads issue des campagnes.
 */
export function pushLead(payload: LeadPayload): void {
  if (!GTM_ID || typeof window === 'undefined') {
    return
  }

  const dataLayer = getDataLayer()
  dataLayer.push({
    transaction_id: payload.transactionId,
    event: 'generate_lead',
    ...(payload.currency ? { currency: payload.currency } : {}),
  })
}

/**
 * Pousse un virtual pageview à chaque navigation client (SPA).
 *
 * Aucun rechargement de page n'a lieu dans une SPA, donc GTM ne voit pas les
 * changements de route : on les lui signale manuellement ici.
 */
export function pushPageView(path: string, title?: string): void {
  if (!GTM_ID || typeof window === 'undefined') {
    return
  }

  const dataLayer = getDataLayer()
  dataLayer.push({
    event: 'page_view',
    page: { path, title: title ?? document.title },
  })
}

/**
 * Pousse un événement `purchase` sur la page de confirmation, une fois la
 * réservation validée. Format « plat » attendu par les balises GTM de l'équipe
 * analytics : `transaction_id`, `value`, `currency`, `items` à la racine.
 */
export function pushPurchase(payload: PurchasePayload): void {
  if (!GTM_ID || typeof window === 'undefined') {
    return
  }

  const dataLayer = getDataLayer()
  dataLayer.push({
    transaction_id: payload.transactionId,
    currency: payload.currency,
    event: 'purchase',
    items: payload.items,
    value: payload.value,
  })
}

function getDataLayer(): DataLayerEntry[] {
  window.dataLayer = window.dataLayer ?? []
  return window.dataLayer
}
